// Compliance Reports hub (/office/compliance-reports). The central page from which
// every compliance evidence report is generated and downloaded as a dated, branded
// PDF for CHAS / ISO / HSE assessors. Reports are generated fresh on demand from live
// data (never stored); the Recent Report History below is the generation audit trail.
//
// One card per report group (CHAS / Engineer competence / LOLER / Quality / Environmental);
// each button generates + downloads the register report. Per-entity "Download ... File"
// buttons (a single subcontractor, engineer or lift) live on those detail pages. The page
// is self-contained (no props, only api.complianceReports) so Session K2 can wrap it with a
// read-only auditor role without changing it.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ApiError, api } from "../../lib/api";
import type { ComplianceReportHistoryItem } from "../../lib/api";
import { ReportDownloadButton } from "./ReportDownloadButton";
import { ConfirmButton } from "../../components/ConfirmButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { formatDate, formatDateTime } from "../../lib/format";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";

// The history's search and sort (design system Session 7a). Client-side over the rows the server
// returned, the term in the URL's ?q=. The auditor's copy of this page (hideGeneratedBy) has the
// same toolbar, and the "By" column is neither searchable nor sortable there, because the column is
// not shown: the staff name must not be discoverable by a search the auditor cannot see the result of.
function historySearch(hideGeneratedBy: boolean): SearchAccessors<ComplianceReportHistoryItem> {
  return [(r) => r.reportRef, (r) => r.reportTypeLabel, ...(hideGeneratedBy ? [] : [(r: ComplianceReportHistoryItem) => r.generatedBy])];
}
const HISTORY_SORT: SortAccessors<ComplianceReportHistoryItem> = {
  ref: (r) => r.reportRef,
  report: (r) => r.reportTypeLabel.toLowerCase(),
  generated: (r) => {
    const t = Date.parse(r.generatedAt);
    return Number.isNaN(t) ? null : t;
  },
  by: (r) => (r.generatedBy ?? "").toLowerCase(),
  internal: (r) => (r.internal ? 1 : 0),
};
const HISTORY_SORT_OPTIONS = [
  { key: "ref", label: "Reference" },
  { key: "report", label: "Report" },
  { key: "generated", label: "Generated" },
];

// Session AOY: the one-line annotation an internally-classified row carries in the OFFICE view,
// for ever. Pure, so the wording and the partial-data cases are unit-tested without a render.
// Returns null for a row that is not marked, so the caller renders nothing at all rather than a
// "not marked" line on every ordinary row.
export function internalAnnotation(r: ComplianceReportHistoryItem): string | null {
  if (!r.internal) return null;
  const who = r.markedInternalBy?.trim();
  // formatDate answers "-" for a missing or unreadable date; here a part is simply left out.
  const whenText = formatDate(r.markedInternalAt);
  const when = whenText === "-" ? "" : whenText;
  // Every part is optional in the DTO, so each is stated only when it is actually known --
  // "Marked internal by Unknown, on " would be worse than the shorter honest sentence.
  const parts = ["Marked internal (verification/testing)"];
  if (who) parts.push(`by ${who}`);
  if (when) parts.push(`on ${when}`);
  const lead = parts.join(" ");
  const note = r.markedInternalNote?.trim();
  return note ? `${lead} · ${note}` : lead;
}

// hideGeneratedBy: drop the "By" (generating staff member) column from the report history.
// Used by the read-only auditor portal (Session K2), where an external assessor must not see
// internal staff names. The server ALSO blanks generatedBy for auditors (defence in depth);
// this hides the column entirely. Office staff (default false) see the full audit trail.
//
// officeControls (Session AOY): show the two staff-only controls -- the test-render toggle and
// the per-row "Mark internal" action. It DEFAULTS TO FALSE so the default is fail-closed: the
// auditor route (<ComplianceReportsPage hideGeneratedBy />) gets neither control without having
// to know they exist, and the office route opts in explicitly. It is a second prop rather than a
// reuse of hideGeneratedBy because the server enforces the two separately (a blanked name on the
// GET, a 403 on the PATCH), and the UI should mirror that rather than imply one flag covers both.
// The server remains the authority either way: this prop grants nothing.
export function ComplianceReportsPage({
  hideGeneratedBy = false,
  officeControls = false,
}: { hideGeneratedBy?: boolean; officeControls?: boolean } = {}) {
  const [history, setHistory] = useState<ComplianceReportHistoryItem[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "forbidden" | "error">("loading");
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  const [mobileCards] = useState(initialMobileLayout);
  // Session AOY. DELIBERATELY NOT PERSISTED -- not localStorage, not sessionStorage, not the
  // URL. Test mode resets to off on every mount, so it cannot be left on across a visit and
  // quietly stop recording a genuine export days later. Within a visit it is unmissable: the
  // advisory strip below, and every download button turning amber and renaming itself.
  const [testRender, setTestRender] = useState(false);
  // The row currently being marked / unmarked, so only its own control shows a busy state.
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);

  const loadHistory = useCallback(() => {
    let live = true;
    setState("loading");
    api.complianceReports
      .list()
      .then((rows) => {
        if (live) {
          setHistory(rows);
          setState("ready");
        }
      })
      .catch((err) => {
        if (!live) return;
        setState(err instanceof ApiError && (err.status === 403 || err.status === 401) ? "forbidden" : "error");
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => loadHistory(), [loadHistory]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("compliance-report-history");
  const shown = sortRows(filterRows(history, search, historySearch(hideGeneratedBy)), HISTORY_SORT, sort).slice(0, 20);
  const matching = filterRows(history, search, historySearch(hideGeneratedBy)).length;

  // Mark or unmark one row, then patch just that row in place. Deliberately NOT a full
  // loadHistory() reload: re-fetching would re-sort and re-slice the list under the office
  // user's cursor at the moment they are reading the annotation they just created.
  const setInternal = async (id: string, internal: boolean) => {
    setMarkingId(id);
    setMarkError(null);
    try {
      const res = await api.complianceReports.markInternal(id, internal);
      setHistory((rows) =>
        rows.map((r) =>
          r.id === id
            ? {
                ...r,
                internal: res.internal,
                markedInternalAt: res.markedInternalAt,
                markedInternalNote: res.markedInternalNote,
                // The route does not echo a display name, and the office user who just pressed
                // the button IS the marker, so "You" is both accurate and the least misleading
                // thing to show until the next load resolves the real name. Cleared on unmark.
                markedInternalBy: res.internal ? "you" : "",
              }
            : r,
        ),
      );
    } catch (e) {
      setMarkError(
        e instanceof ApiError && e.status === 403
          ? "Only office staff and administrators can classify a report history entry."
          : "Could not update that entry. Please try again in a moment.",
      );
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        className="mb-6"
        title="Compliance reports"
        subtitle="Generate and download compliance evidence reports. All reports are dated and reference live platform data at the time of generation."
      />

      {/* Session AOY: test-render mode. Office staff only, off on every page load, and loud while
          it is on -- see the toggle's own comment in the component body for why it is never
          persisted. The point of the feature is that verifying a report change (a template edit,
          a new column, a Playwright render) no longer leaves an entry in the trail an external
          assessor reads as compliance activity. */}
      {officeControls && (
        <div
          className={`mb-4 rounded-ele border p-4 ${
            testRender ? "border-amber-500 bg-amber-500/10" : "border-ele-hairline bg-ele-surface"
          }`}
        >
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={testRender}
              onChange={(e) => setTestRender(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-none accent-amber-500"
            />
            <span>
              <span className="block font-heading text-sm tracking-wide text-ele-strong">
                Test render (not recorded in the report history)
              </span>
              <span className="mt-1 block text-xs text-ele-body">
                For checking that a report still renders correctly after a change. The PDF is generated from live data
                exactly as normal, but no entry is written to the generation audit trail below and the Compliance Status
                Report is not filed to Drive. Use it for testing only: a genuine compliance export must be recorded, so
                leave this off for real work.
              </span>
              {testRender && (
                <span role="status" className="mt-2 block font-heading text-xs tracking-wide text-amber-400">
                  Test render is ON. Every download on this page is unrecorded until you turn it off or reload.
                </span>
              )}
            </span>
          </label>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <ReportCard
          title="Compliance Status Report (consolidated)"
          blurb="A single internal compliance status snapshot for an auditor, insurer or the HSE: portfolio + LOLER status, SAFed status, engineer competence, and the open RIDDOR and modification advisory flags. Regulatory advisories are guidance for review, not legal advice."
        >
          <ReportDownloadButton reportType="ComplianceStatusReport" label="Download Compliance Status Report" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="CHAS / Subcontractor Management"
          blurb="Used for CHAS pre-qualification and principal contractor requests."
        >
          <ReportDownloadButton reportType="SubcontractorRegister" label="Download Approved Subcontractor Register" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="Engineer Competence (CHAS / ISO 9001 / ISO 45001)"
          blurb="Used for CHAS, ISO 9001 competence evidence, and client pre-qualification."
        >
          <ReportDownloadButton reportType="EngineerCompetenceRegister" label="Download Engineer Competence Register" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="LOLER / Statutory (HSE)"
          blurb="Statutory record of thorough examination status across the portfolio."
        >
          <ReportDownloadButton reportType="LOLERComplianceSummary" label="Download LOLER Compliance Summary" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="Insurer findings (third-party inspections)"
          blurb="What an insurer's inspector reported against a lift, in their own words, and where each finding stands. Not LOLER examination records and never counted in the LOLER figures."
        >
          <ReportDownloadButton reportType="InsurerFindingsRegister" label="Download Insurer Findings Register" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="Accidents and Enforcement (CHAS / RIDDOR / HSE)"
          blurb="The registers behind CHAS's accident-recording and enforcement-notice questions: any RIDDOR reportable accidents, and any HSE or Local Authority enforcement notices or prosecutions."
        >
          <ReportDownloadButton reportType="AccidentIncidentRegister" label="Download Accident and Incident Register" dryRun={testRender} />
          <ReportDownloadButton reportType="EnforcementNoticesRegister" label="Download Enforcement Notices and Prosecutions Register" dryRun={testRender} />
        </ReportCard>

        <ReportCard title="Quality Management (ISO 9001)" blurb="ISO 9001 clause 10.2 evidence.">
          <ReportDownloadButton reportType="ComplaintsRegister" label="Download Complaints and Corrective Actions Register" dryRun={testRender} />
        </ReportCard>

        <ReportCard title="Environmental Management (ISO 14001)" blurb="ISO 14001 evidence summary.">
          <ReportDownloadButton reportType="EnvironmentalSummary" label="Download Environmental Management Summary" dryRun={testRender} />
        </ReportCard>

        <ReportCard
          title="ISO 9001 Governance (clauses 9.2 and 9.3)"
          blurb="Internal audit and management review records for ISO 9001 evidence."
        >
          <ReportDownloadButton reportType="InternalAuditRegister" label="Download Internal Audit Register" dryRun={testRender} />
          <ReportDownloadButton reportType="ManagementReviewRegister" label="Download Management Review Register" dryRun={testRender} />
        </ReportCard>
      </div>

      <section className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-3 font-heading text-lg text-ele-blue">Recent report history</h2>
        <p className="mb-3 text-xs text-ele-body">
          The generation audit trail (the last 20 reports). Reports are generated fresh on demand and not stored.
        </p>
        {/* Session AOY: the auditor is TOLD that internal renders are excluded from their view.
            This is the judgement the whole feature turns on. Excluding the platform's own
            verification renders keeps the evidence readable; excluding them SILENTLY would make
            this a curated history, which is a worse assessment finding than a complete one
            carrying a few labelled internal entries. Stated as standing policy, so it is
            accurate whether or not any row is currently classified, and it needs no count (and
            therefore no change to the response shape, which a half-deployed SPA bundle would
            have to survive). The office sees every row either way. */}
        {!officeControls && (
          <p className="mb-3 text-xs text-ele-body">
            Internal platform verification and testing renders are not listed here. East London Elevators Ltd retains
            the complete unfiltered record of every report generated, available on request.
          </p>
        )}
        {markError && (
          <p role="alert" className="mb-3 text-xs text-ele-danger">
            {markError}
          </p>
        )}
        {state === "ready" && history.length > 0 && (
          <ListToolbar
            className="mb-3"
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={hideGeneratedBy ? "Search reference or report..." : "Search reference, report or who generated it..."}
            searchLabel="Search report history"
            resultCount={{ shown: Math.min(matching, 20), total: Math.min(history.length, 20), noun: "reports" }}
            sort={{ state: sort, options: HISTORY_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
            onClear={() => setSearch("")}
          />
        )}
        {state === "loading" ? (
          <Loader label="Loading the report history..." shape="inline" />
        ) : state === "forbidden" ? (
          <p className="text-sm text-ele-body">The report history is available to office staff.</p>
        ) : state === "error" ? (
          <p role="alert" className="text-sm text-ele-strong">
            Could not load the report history. Please try again in a moment.
          </p>
        ) : history.length === 0 ? (
          <p className="text-sm text-ele-body">No reports have been generated yet.</p>
        ) : shown.length === 0 ? (
          <EmptyState compact role="status" message={`No reports match "${search.trim()}".`} />
        ) : mobileCards ? (
          // Verified before converting (this table has no mobile chevron and only 3-4 columns,
          // so it is not an automatic case): the Report column carries labels up to 44
          // characters ("Complaints and Corrective Actions Register") and Reference carries a
          // 21-character code, both squeezed into a ~30-34%-of-viewport fixed-layout column
          // with no reclaimed space -- the same truncation shape as the Invoices list, just
          // without the disclosure chevron. The card restores both in full and moves "By" (when
          // shown at all) to the details block rather than dividing the row further.
          <MobileCardList label="Report history">
            {shown.map((r) => {
              const annotation = internalAnnotation(r);
              return (
                <MobileListCard
                  key={r.id}
                  primary={r.reportTypeLabel}
                  secondary={<span className="font-mono">{r.reportRef}</span>}
                  meta={formatDateTime(r.generatedAt)}
                  details={
                    // The annotation belongs in the details block for the same reason "By" does:
                    // it is a full-width sentence, not a column.
                    !hideGeneratedBy || annotation ? (
                      <span className="block">
                        {!hideGeneratedBy && <span className="block">Generated by {r.generatedBy}</span>}
                        {annotation && <span className="mt-1 block text-amber-400">{annotation}</span>}
                      </span>
                    ) : undefined
                  }
                  // The `actions` slot carries its own 44px tap target and sits OUTSIDE the
                  // tap-through body, which is why a button is valid here (see MobileListCard).
                  actions={officeControls ? <InternalRowControl r={r} busy={markingId === r.id} onSet={setInternal} /> : undefined}
                />
              );
            })}
          </MobileCardList>
        ) : (
          <div className="overflow-hidden">
            <table className="ele-list-table w-full text-left text-sm">
              {/* Session AOY rebalanced these for the extra column rather than letting the
                  fixed layout squeeze the existing four (the TABLE RULE: the table fits its
                  container and truncates, so a fifth column has to be paid for). The auditor
                  never gets either of the two staff columns, so their three keep the full
                  width they had. */}
              <colgroup>
                <col style={{ width: hideGeneratedBy ? "30%" : officeControls ? "20%" : "24%" }} />
                <col style={{ width: hideGeneratedBy ? "44%" : officeControls ? "30%" : "34%" }} />
                <col style={{ width: hideGeneratedBy ? "26%" : officeControls ? "18%" : "22%" }} />
                {!hideGeneratedBy && <col className="max-md:hidden" style={{ width: officeControls ? "16%" : "20%" }} />}
                {officeControls && <col style={{ width: "16%" }} />}
              </colgroup>
              <thead>
                <tr className="border-b border-ele-purple-accent text-xs uppercase tracking-widest text-ele-blue">
                  <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Report" colKey="report" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Generated" colKey="generated" sort={sort} onToggle={sort.toggle} />
                  {!hideGeneratedBy && <SortableHeader label="By" colKey="by" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />}
                  {officeControls && <SortableHeader label="Internal" colKey="internal" sort={sort} onToggle={sort.toggle} />}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const annotation = internalAnnotation(r);
                  return (
                    <tr key={r.id} className="border-b border-ele-hairline">
                      <td className="py-2 pr-3 font-mono text-xs text-ele-strong">{r.reportRef}</td>
                      <td className="py-2 pr-3 text-ele-strong">
                        {r.reportTypeLabel}
                        {/* The annotation is a quiet secondary line under the label, never a
                            replacement for it: a classified row keeps its reference, its type,
                            its timestamp and its generator in the office's view, permanently.
                            Amber (advisory), never red -- nothing here is a fault. */}
                        {annotation && (
                          <span className="mt-0.5 block whitespace-normal text-xs text-amber-400">{annotation}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-ele-body">{formatDateTime(r.generatedAt)}</td>
                      {!hideGeneratedBy && <td className="max-md:hidden py-2 pr-3 text-ele-body">{r.generatedBy}</td>}
                      {officeControls && (
                        <td className="py-2 pr-3">
                          <InternalRowControl r={r} busy={markingId === r.id} onSet={setInternal} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// Session AOY: the per-row internal classification control, office and admin only.
//
// THE TWO DIRECTIONS ARE DELIBERATELY ASYMMETRIC, and it is not an oversight.
//
//   MARKING confirms twice, through the branded two-step ConfirmButton. It is a consequential
//   action under the BRANDED CONFIRM rule: its whole effect is to WITHHOLD an entry from an
//   external assessor's view of a compliance audit trail, and the bad outcome -- classifying a
//   genuine compliance export as a test -- takes real evidence out of that view. A stray click
//   must not be able to do that. ConfirmButton also disarms on blur, on Escape and after a few
//   seconds, so an armed row never lingers.
//
//   RESTORING is a single click. It can only ever ADD a row back into the auditor's view, so
//   there is no outcome to protect against: the worst a stray click does is make the trail MORE
//   complete. Gating it behind a second confirm would put friction on the corrective action and
//   none on nothing.
//
// Neither direction deletes anything, and the row never leaves this table.
function InternalRowControl({
  r,
  busy,
  onSet,
}: {
  r: ComplianceReportHistoryItem;
  busy: boolean;
  onSet: (id: string, internal: boolean) => void;
}) {
  if (r.internal) {
    return (
      <button
        type="button"
        onClick={() => onSet(r.id, false)}
        disabled={busy}
        aria-busy={busy}
        className="min-h-[44px] rounded-ele border border-ele-hairline px-2 py-1 text-xs text-ele-body disabled:opacity-60 md:min-h-0"
        title="Show this entry to external auditors again"
      >
        {busy ? "Restoring..." : "Restore"}
      </button>
    );
  }
  return (
    <ConfirmButton
      onConfirm={() => onSet(r.id, true)}
      label="Mark internal"
      confirmLabel="Confirm?"
      busy={busy}
      busyLabel="Marking..."
      className="min-h-[44px] rounded-ele border border-ele-hairline px-2 py-1 text-xs text-ele-body disabled:opacity-60 md:min-h-0"
      armedClassName="!text-amber-400 font-semibold"
      title="Classify this entry as an internal verification or testing render, and exclude it from the external auditor view. It stays here permanently."
    />
  );
}

// One report-group card: a heading, a one-line blurb on what the report is used for,
// and one or more download buttons (passed as children).
function ReportCard({ title, blurb, children }: { title: string; blurb: string; children: ReactNode }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="font-heading text-lg text-ele-blue">{title}</h2>
      <p className="mb-3 mt-1 text-xs text-ele-body">{blurb}</p>
      <div className="flex flex-wrap gap-3">{children}</div>
    </div>
  );
}
